import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import {
  TipoContrato,
  CreateTipoContratoRequest,
  UpdateTipoContratoRequest
} from '../models/tipo-contrato.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { TipoContratoMapper } from '../models/tipo-contrato.mapper';
import { TipoContratoDto } from '../models/tipo-contrato.dto';

@Injectable({
  providedIn: 'root'
})
export class TipoContratoService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/TipoContrato`;

  // Envelope común de la API
  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  // Normaliza claves del backend -> modelo de UI
  private normalizeTipoContrato(raw: TipoContratoDto): TipoContrato {
    return TipoContratoMapper.fromDto(raw);
  }

  private normalizeList(list: TipoContratoDto[] | TipoContrato[]): TipoContrato[] {
    return TipoContratoMapper.fromDtoList(list as TipoContratoDto[]);
  }

  /**
   * Obtiene todos los tipos de contrato
   */
  getTiposContrato(): Observable<TipoContrato[]> {
    return this.http.get<ApiEnvelope<TipoContratoDto[]>>(`${this.baseUrl}/GetAll`).pipe(
      this.unwrapResponse<TipoContratoDto[]>(),
      map((response: TipoContratoDto[]) => this.normalizeList(response))
    );
  }

  /**
   * Obtiene un tipo de contrato por ID
   */
  getTipoContratoById(id: number): Observable<TipoContrato> {
    return this.http.get<ApiEnvelope<TipoContratoDto>>(`${this.baseUrl}/GetByID?id=${id}`).pipe(
      this.unwrapResponse<TipoContratoDto>(),
      map((response: TipoContratoDto) => this.normalizeTipoContrato(response))
    );
  }

  /**
   * Crea un nuevo tipo de contrato
   */
  createTipoContrato(request: CreateTipoContratoRequest): Observable<TipoContrato> {
    return this.http.post<ApiEnvelope<TipoContratoDto>>(`${this.baseUrl}/Create`, request).pipe(
      this.unwrapResponse<TipoContratoDto>(),
      map((response: TipoContratoDto) => this.normalizeTipoContrato(response)),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza un tipo de contrato existente
   */
  updateTipoContrato(id: number, request: UpdateTipoContratoRequest): Observable<TipoContrato> {
    return this.http.put<ApiEnvelope<TipoContratoDto>>(`${this.baseUrl}/${id}`, request).pipe(
      this.unwrapResponse<TipoContratoDto>(),
      map((response: TipoContratoDto) => this.normalizeTipoContrato(response))
    );
  }

  /**
   * Elimina un tipo de contrato
   */
  deleteTipoContrato(id: number): Observable<boolean> {
    return this.http.delete<ApiEnvelope<string>>(`${this.baseUrl}/${id}`).pipe(
      this.unwrapResponse<string>(),
      map(() => true)
    );
  }
}
